import { useMutation, useQueryClient } from '@tanstack/react-query'
import { AlertCircle, Check, Save } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { canonicalize } from '@/modules/shared/json-canonicalize'
import { requestCenterApi } from '@/services/request-center-api'
import type { FormSchema } from '@kfolks/types'
import { useFormBuilderState } from './FormBuilderState'

/**
 * Persistent header bar for the form builder. Renders above the tabs so the
 * Save button is reachable from any tab (Visual / Preview / History / Raw
 * JSON).
 *
 * **Save action** (per the form-builder capability spec): calls the
 * existing admin endpoint `PUT /requests/types/:id` (no new persistence
 * endpoint introduced) with the canonicalized payload — `formSchema` is
 * canonicalized via the shared helper so key-order trivia doesn't churn
 * the persisted value, and `summaryTemplate` passes through (already
 * normalised to `null` when empty by `setSummaryTemplate`).
 *
 * **Save gate**: the button is disabled when `validation.hasErrors` is
 * true (client-side gate). The backend re-runs the same validators
 * authoritatively on save; this gate is a UX hint, not the load-bearing
 * check. Warnings (severity `warning`) do NOT block the save — only
 * errors do.
 *
 * **Workflow config**: the form builder authors only `formSchema` +
 * `summaryTemplate`. The PUT payload deliberately omits `workflowConfig`
 * so the backend's partial-update semantics leave it untouched (the
 * workflow builder owns that field on its own route).
 *
 * **Status**: success / error feedback inline to the right of the button.
 * No toast library — the existing FE doesn't use one. A persistent
 * inline status is sufficient and accessible.
 *
 * The mutation invalidates the `request-types` query on success so the
 * builder reflects the persisted payload (and so the parent admin page
 * refreshes the list).
 */
export function FormBuilderHeader({ typeId, typeName }: { typeId: string | undefined; typeName?: string | undefined }) {
	const { formSchema, summaryTemplate, validation } = useFormBuilderState()
	const queryClient = useQueryClient()

	const save = useMutation({
		mutationFn: async () => {
			if (!typeId) throw new Error('Missing request type id — cannot save')
			const payload = buildSavePayload(formSchema, summaryTemplate)
			return requestCenterApi.updateRequestType(typeId, payload)
		},
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: ['request-types'] })
		},
	})

	const blocked = validation.hasErrors
	const blockedReason = blocked
		? `${countErrors(validation.errorsByFieldId, validation.topLevelErrors)} validation error(s) — fix to enable save`
		: null

	return (
		<header className="flex flex-wrap items-center justify-between gap-2 rounded-md border bg-card px-3 py-2">
			<div className="min-w-0">
				<p className="text-xs uppercase tracking-wide text-muted-foreground">Form builder</p>
				{typeName ? (
					<p className="truncate text-base font-semibold">{typeName}</p>
				) : typeId ? (
					<p className="truncate font-mono text-xs text-muted-foreground/80">type id: {typeId}</p>
				) : null}
			</div>
			<div className="flex items-center gap-2">
				<SaveStatus
					isPending={save.isPending}
					isSuccess={save.isSuccess}
					error={save.error}
					blockedReason={blockedReason}
				/>
				<Button
					onClick={() => save.mutate()}
					disabled={blocked || save.isPending || !typeId}
					aria-label="Save form schema"
				>
					<Save className="size-4" />
					{save.isPending ? 'Saving…' : 'Save'}
				</Button>
			</div>
		</header>
	)
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function buildSavePayload(
	formSchema: FormSchema,
	summaryTemplate: string | null,
): { formSchema: FormSchema; summaryTemplate: string | null } {
	// Canonicalize formSchema so key-order trivia doesn't churn the persisted
	// value (Visual-tab edits shuffle keys via React state); the round-trip
	// equality contract from design D5 is enforced this way.
	const canonical = canonicalize(formSchema) as unknown as FormSchema
	return { formSchema: canonical, summaryTemplate }
}

function countErrors(byField: Record<string, string[]>, topLevel: ReadonlyArray<string>): number {
	let n = topLevel.length
	for (const list of Object.values(byField)) n += list.length
	return n
}

function SaveStatus({
	isPending,
	isSuccess,
	error,
	blockedReason,
}: {
	isPending: boolean
	isSuccess: boolean
	error: unknown
	blockedReason: string | null
}) {
	if (isPending) return null
	if (error) {
		return (
			<span role="alert" className="flex items-center gap-1 text-sm text-destructive">
				<AlertCircle className="size-4" /> {messageFor(error)}
			</span>
		)
	}
	if (blockedReason) {
		return (
			<span className="flex items-center gap-1 text-xs text-destructive" data-testid="save-blocked-reason">
				<AlertCircle className="size-4" /> {blockedReason}
			</span>
		)
	}
	if (isSuccess) {
		return (
			<span className="flex items-center gap-1 text-sm text-emerald-600" data-testid="save-success">
				<Check className="size-4" /> Saved
			</span>
		)
	}
	return null
}

function messageFor(err: unknown): string {
	if (err instanceof Error && err.message) return err.message
	return 'Save failed — try again'
}
